import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  merge,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { BusinessAccount } from '@/app/common/general-types/business-account.dto';
import { FiltersGenericDto } from '@/app/services/user/user.service';
import { BusinessAccountService, BrandResponseDto } from '@/app/services/business-account/business-account.service';
import { ImageService } from '@/app/services/image/image.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { EmptyStateComponent } from '@/app/atoms/empty-state/empty-state.component';
import { formatDistanceToNow } from 'date-fns';
import { BrandInsightsState } from '../brand-insights.state';
import { BrandInsightsSection } from '../../../insights-nav';

@Component({
  selector: 'ins-brand-list',
  imports: [RouterOutlet, EmptyStateComponent],
  templateUrl: './brand-list.html',
  styleUrl: './brand-list.css',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BrandList implements OnInit {
  private static readonly PAGE_SIZE = 25;
  private static readonly SCROLL_LOAD_THRESHOLD_PX = 80;

  brandFilters = signal<FiltersGenericDto>({});
  pageNumber = signal(0);

  brands = signal<BusinessAccount[] | undefined>(undefined);
  brandsCount = signal(0);
  brandsLoading = signal(false);
  brandsLoadingMore = signal(false);
  searchQuery = signal('');
  selectedBrandId = signal<string | undefined>(undefined);

  private searchTrigger = new Subject<string>();
  private searchSessionId = signal(0);
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private businessAccountService = inject(BusinessAccountService);
  private toastNotificationService = inject(ToastNotificationService);
  private brandState = inject(BrandInsightsState);
  readonly imageService = inject(ImageService);

  ngOnInit(): void {
    this.syncSelectedFromRoute();
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.syncSelectedFromRoute());

    this.brandFilters.set({
      take: BrandList.PAGE_SIZE,
      skip: 0,
      sortField: 'createdDate',
      sortOrder: 'DESC',
    });

    merge(of(''), this.searchTrigger.pipe(debounceTime(300), distinctUntilChanged()))
      .pipe(
        tap(() => {
          this.pageNumber.set(0);
          this.brandsLoadingMore.set(false);
          this.searchSessionId.update((n) => n + 1);
        }),
        switchMap((term) => {
          this.brandsLoading.set(true);
          return this.businessAccountService.getInsightsBrands(this.buildFilters(0, term)).pipe(
            tap({
              next: () => this.brandsLoading.set(false),
              error: () => this.brandsLoading.set(false),
            }),
            catchError((err) => {
              console.error('Error fetching brands:', err);
              this.toastNotificationService.alert({
                title: 'Error fetching brands',
                message: 'Failed to retrieve brands.',
                type: 'error',
                icon: 'error',
              });
              return of({ count: 0, data: [] } satisfies BrandResponseDto);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.brands.set(response.data);
        this.brandsCount.set(response.count);
      });
  }

  timeSinceCreated(createdDate: Date | string | undefined): string {
    if (createdDate == null) {
      return '—';
    }
    const d = typeof createdDate === 'string' ? new Date(createdDate) : createdDate;
    if (Number.isNaN(d.getTime())) {
      return '—';
    }
    return formatDistanceToNow(d, { addSuffix: true });
  }

  onSearchInput(value: string): void {
    this.searchQuery.set(value);
    this.searchTrigger.next(value.trim());
  }

  onListScroll(event: Event): void {
    const el = event.target as HTMLElement | null;
    if (!el) {
      return;
    }
    const { scrollTop, clientHeight, scrollHeight } = el;
    if (scrollHeight - scrollTop - clientHeight > BrandList.SCROLL_LOAD_THRESHOLD_PX) {
      return;
    }
    this.loadMoreIfNeeded();
  }

  isSelectedBrand(brand: BusinessAccount): boolean {
    return this.selectedBrandId() === brand.id;
  }

  onBrandSelect(brand: BusinessAccount): void {
    this.selectedBrandId.set(brand.id);
    this.brandState.setBrand(brand);
    void this.router.navigate(['/insights/brand', brand.id, this.currentSection()]);
  }

  hasMore(): boolean {
    const list = this.brands();
    if (list === undefined) {
      return false;
    }
    return list.length < this.brandsCount();
  }

  private currentSection(): BrandInsightsSection {
    const match = this.router.url.match(/\/insights\/brand\/[^/]+\/([^/?]+)/);
    return (match?.[1] as BrandInsightsSection) || 'profile';
  }

  private loadMoreIfNeeded(): void {
    if (this.brandsLoading() || this.brandsLoadingMore()) {
      return;
    }
    if (this.brands() === undefined || !this.hasMore()) {
      return;
    }

    const nextPage = this.pageNumber() + 1;
    const term = this.searchQuery().trim();
    const session = this.searchSessionId();
    this.brandsLoadingMore.set(true);

    this.businessAccountService
      .getInsightsBrands(this.buildFilters(nextPage, term))
      .pipe(
        catchError((err) => {
          console.error('Error fetching more brands:', err);
          this.toastNotificationService.alert({
            title: 'Error fetching brands',
            message: 'Failed to load more brands.',
            type: 'error',
            icon: 'error',
          });
          return of({ count: 0, data: [] } satisfies BrandResponseDto);
        }),
        finalize(() => this.brandsLoadingMore.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (session !== this.searchSessionId()) {
          return;
        }
        if (response.data.length === 0) {
          this.brandsCount.set((this.brands() ?? []).length);
          return;
        }
        this.brands.update((prev) => [...(prev ?? []), ...response.data]);
        this.pageNumber.set(nextPage);
      });
  }

  private buildFilters(page: number, searchTerm: string): FiltersGenericDto {
    const base = this.brandFilters();
    const term = searchTerm.trim();
    return {
      ...base,
      take: BrandList.PAGE_SIZE,
      skip: page * BrandList.PAGE_SIZE,
      searchTerm: term || undefined,
    };
  }

  private syncSelectedFromRoute(): void {
    const businessAccountId = this.route.firstChild?.snapshot.paramMap.get('businessAccountId');
    this.selectedBrandId.set(businessAccountId ?? undefined);
  }
}
